import { ActionIcon, Tooltip } from '@mantine/core';
import { IconEdit, IconSend, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MentionTextarea } from 'src/Components/MentionTextarea';
import UserAvatar from 'src/Components/NavBar/UserAvatar';
import { RowActions } from 'src/Components/Shared/RowActions';
import useFetchUser from 'src/Hooks/Queries/useFetchUser';
import { CommentDto } from 'src/Services/API/generated';
import { renderTextWithMentions } from 'src/Utils/renderMentions';
import { renderWhatsappFormatting } from 'src/Utils/renderWhatsappFormatting';

import styles from './CommentItem.module.scss';

export type CommentItemProps = {
  comment: CommentDto;
  highlighted?: boolean;
  onEdit: (commentId: string, message: string) => void;
  onDelete: (commentId: string) => void;
  isEditLoading?: boolean;
};

const CommentItem = ({
  comment,
  highlighted,
  onEdit,
  onDelete,
  isEditLoading,
}: CommentItemProps) => {
  const { t } = useTranslation();

  const [state, setState] = useState<'view' | 'edit'>('view');
  const [text, setText] = useState(comment.text);

  const date = dayjs(comment.createdAt).format('L');
  const time = dayjs(comment.createdAt).format('LT');
  const metadataLabel = `${comment.author.firstName} ${comment.author.lastName} · ${t('community.postDate', { date, time })}`;

  const toggleState = () => {
    setState(s => (s === 'view' ? 'edit' : 'view'));
  };

  const handleEdit = () => {
    const trim = text.trim();

    if (!trim) return;

    onEdit(comment.id, trim);
    setState('view');
  };

  const handleDelete = () => {
    onDelete(comment.id);
  };

  const { data: me } = useFetchUser();
  const isFromMyselfAndNew =
    comment.author.id === me?.id && dayjs().diff(dayjs(comment.createdAt), 'seconds') < 30;

  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isFromMyselfAndNew) {
      containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, []);

  const canEditAndDelete = comment.author.id === me?.id;

  const commentContent = renderWhatsappFormatting(comment.text, plain =>
    renderTextWithMentions(plain, styles.mention)
  );

  return (
    <div
      ref={containerRef}
      className={styles.container}
      style={{ backgroundColor: isFromMyselfAndNew || highlighted ? '#F8FFC1' : undefined }}
    >
      <div className={styles.avatar}>
        <UserAvatar size={36} user={comment.author} />
      </div>

      <div className={styles.middleColumn}>
        <Tooltip label={metadataLabel}>
          <div className={styles.author}>
            {comment.author.firstName}{' '}
            <span className={styles.metadata}>· {dayjs(comment.createdAt).fromNow()}</span>
          </div>
        </Tooltip>

        {state === 'view' ? <p className={styles.text}>{commentContent}</p> : null}

        {state === 'edit' ? (
          <div className={styles.edit}>
            <MentionTextarea
              value={text}
              onChange={setText}
              placeholder={t('community.comments.placeholder', 'Écrire un commentaire')}
            />

            <ActionIcon
              variant="outline"
              className={styles.icon}
              onClick={handleEdit}
              loading={isEditLoading}
            >
              <IconSend size={20} />
            </ActionIcon>
          </div>
        ) : null}
      </div>

      {canEditAndDelete ? (
        <div className={styles.menu}>
          <RowActions
            actions={[
              {
                key: 'edit',
                label:
                  state === 'edit'
                    ? t('common.cancelEdit', "Annuler l'édition")
                    : t('common.edit', 'Modifier'),
                icon: <IconEdit size={18} />,
                onClick: toggleState,
              },
              {
                key: 'delete',
                label: t('common.delete', 'Supprimer'),
                icon: <IconTrash size={18} />,
                variant: 'danger',
                onClick: handleDelete,
              },
            ]}
          />
        </div>
      ) : null}
    </div>
  );
};

export default CommentItem;
